import React, { useMemo } from 'react';
import { brandIsNetwork } from '../../utils/plans';
import { Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useUser } from '../../hooks/useUser';
import {
  NavBarItem,
  NavLinkItem,
  NavLinks,
} from '../Header/configuration/navs';
import { isBrand, isBrandSales, isInternal, isRMN, isExpert, titleizeName, initialsFromName } from '../../utils';
import { SystemRoles } from '../../providers/roles';
import { useRmnBrand } from '../../layouts/RmnBrand/RmnBrandContext';
import { portalBasePath } from '../../utils/portalPath';
import { useNetworkScope } from '../../hooks/useNetworkScope';
import { BrandNavEntry, BrandNavItems, NavVisibilityCtx } from './brandNav';

// Brand-portal roles (BRAND_ADMIN / BRAND_MEMBER / BRAND_TEAM_SALES) get the
// TDC-1499 nav from brandNav.tsx — explicit title/icon fields, per-role
// visibility, and the same parent/accordion-children shape as the internal
// nav, so the rail renders both identically. Internal staff inside a
// brand-portal workspace (/portal/:brandSlug/*, RmnBrandContext resolved)
// get the WORKSPACE rail: the network sections of that same config
// re-rooted under the slug, a workspace card naming the brand, and an
// "Exit workspace" control back to the brand's admin page. Every other role
// keeps the legacy navs.tsx config verbatim, rendered as a single unlabeled
// section:
// that config stores each item's icon + text as a composed JSX `label`, so we
// derive the pieces the rail needs (icon element, plain-text title, Beta
// flag) from the label rather than duplicating a second nav source. If a
// legacy item ever gains explicit `title` / `icon` / `beta` fields they take
// precedence.

export interface SideNavChild {
  key: string;
  title: string;
  icon: React.ReactNode | null;
  link: string;
  external?: boolean;
}

export interface SideNavItem {
  key: string;
  title: string;
  icon: React.ReactNode | null;
  link: string;
  beta: boolean;
  children: SideNavChild[];
}

export interface SideNavSection {
  key: string;
  // Uppercase group header; null = unlabeled block (legacy navs, Dashboard).
  label: string | null;
  items: SideNavItem[];
}

export interface SideNavWorkspace {
  name: string;
  logo?: string | null;
  // Internal staff's way out of a brand-portal workspace (rendered as an
  // icon button on the card; the collapsed chip becomes the button).
  exit?: { link: string; label: string };
}

export interface SideNavAccount {
  name: string;
  role: string;
  avatar: string | null;
  initials: string;
}

// Recursively pull the visible text out of a label node (walks Fragments and
// Typography children). Used only for the plain-text rail label.
function nodeText(node: React.ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join("");
  if (React.isValidElement(node)) return nodeText((node.props as any).children);
  return "";
}

// A label is either a Fragment ([Icon, <Typography>Text</Typography>]) or a
// bare <Typography>Text</Typography> (some dropdown children have no icon).
function labelChildren(label: React.ReactNode): React.ReactNode[] {
  if (React.isValidElement(label) && label.type === React.Fragment) {
    return React.Children.toArray((label.props as any).children);
  }
  return React.Children.toArray(label);
}

// The icon is the first child that isn't the text <Typography>.
function labelIcon(label: React.ReactNode): React.ReactNode | null {
  const kids = labelChildren(label);
  const icon = kids.find(
    (k) => React.isValidElement(k) && k.type !== Typography
  );
  return icon ?? null;
}

// "Video ProductionBeta" -> { title: "Video Production", beta: true }
function labelTitleBeta(label: React.ReactNode): { title: string; beta: boolean } {
  const raw = nodeText(label).replace(/\s+/g, " ").trim();
  const beta = /beta/i.test(raw);
  const title = raw.replace(/beta/i, "").trim();
  return { title, beta };
}

function toChild(opt: NavLinkItem): SideNavChild {
  const { title } = labelTitleBeta(opt.label);
  return {
    key: opt.link || title,
    title: (opt as any).title ?? title,
    icon: (opt as any).icon ?? labelIcon(opt.label),
    link: opt.link || "",
    external: opt.external,
  };
}

function toItem(item: NavBarItem): SideNavItem {
  const { title, beta } = labelTitleBeta(item.label);
  return {
    key: item.link || title,
    title: (item as any).title ?? title,
    icon: (item as any).icon ?? labelIcon(item.label),
    link: item.link || "",
    beta: (item as any).beta ?? beta,
    children: (item.dropdownOptions || []).map(toChild),
  };
}

const SOW_LINK = "/video-production/sow";

// brandNav config -> rail items, applying the visibility predicates.
// `prefix` re-roots every link (the workspace rail lives under
// /portal/:brandSlug).
// A parent lands on its first VISIBLE child, so a plan-gated first child
// (Intelligence > Brand Intelligence) doesn't strand other plans on a page
// they can't open; `link` is the fallback for childless entries.
function brandNavItems(entries: BrandNavEntry[], ctx: NavVisibilityCtx, prefix = ''): SideNavItem[] {
  return entries
    .filter((i) => !i.visible || i.visible(ctx))
    .map((i) => {
      const children = (i.children || []).filter((c) => !c.visible || c.visible(ctx));
      return {
        key: i.key,
        title: i.title,
        icon: i.icon,
        link: `${prefix}${children.length ? children[0].link : i.link}`,
        beta: !!i.beta,
        children: children.map((c) => ({
          key: c.key,
          title: c.title,
          icon: c.icon ?? null,
          link: `${prefix}${c.link}`,
        })),
      };
    });
}

// Central data hook for the rail: resolves the role's nav sections, plus the
// workspace label, account block and help link. One source of truth so the
// desktop rail and mobile drawer stay in sync.
export function useSideNavData() {
  const location = useLocation();
  const { user, role, brand } = useUser();
  // Resolved only under /portal/:brandSlug (RmnBrandLayout); the context
  // default outside it is brand:null. Internal staff inside a workspace get
  // the trimmed workspace rail; brand roles get their own full rail, which
  // since TDC-1609 is prefixed with their brand's portal base path.
  const { brand: networkBrand } = useRmnBrand();
  const workspaceBrand = networkBrand && isInternal(role) ? networkBrand : null;
  // The single source of the prefix for both branches. Rail links and route
  // mounts MUST move together: isLinkActive below compares each link against
  // location.pathname, so a rail built without the prefix silently stops
  // highlighting the moment the routes are slugged.
  const { basePath } = useNetworkScope();

  const sections = useMemo<SideNavSection[]>(() => {
    if (workspaceBrand) {
      // Internal staff open a workspace to see what the CLIENT sees, so they
      // get the client's full rail — not a trimmed catalog-only set. The pages
      // behind Dashboard / Production / Content / Experts scope their fetches
      // to useNetworkScope().brandId, which resolves to this workspace brand.
      // Catalog > Brands stays network-plan-only via the config's own predicate.
      const ctx: NavVisibilityCtx = {
        role: role as any,
        user,
        isNetwork: brandIsNetwork(workspaceBrand),
        plan: (workspaceBrand as any).plan || null,
      };
      const items = brandNavItems(
        BrandNavItems,
        ctx,
        portalBasePath(workspaceBrand.slug)
      );
      return [{ key: 'workspace', label: null, items }];
    }

    if (isBrand(role) || isBrandSales(role)) {
      const brands: any[] = Array.isArray(brand) ? brand : [];
      const ownNetwork = brands.find(brandIsNetwork);
      const ctx: NavVisibilityCtx = {
        role: role as any,
        user,
        isNetwork: isRMN(user),
        plan: (ownNetwork && ownNetwork.plan) || (brands[0] && brands[0].plan) || null,
      };
      // Same single unlabeled section the legacy branch produces, so the rail
      // renders brand and internal navs through the identical code path.
      return [
        { key: 'main', label: null, items: brandNavItems(BrandNavItems, ctx, basePath) },
      ];
    }

    const legacyNav = NavLinks as Record<string, NavBarItem[] | undefined>;
    let links: NavBarItem[] = (role && legacyNav[role]) || [];

    // RMN users don't get the SOW child (mirrors the old HeaderV2 rule;
    // brand roles never reach this branch anymore).
    if (isRMN(user)) {
      links = links.map((item) => ({
        ...item,
        dropdownOptions: item.dropdownOptions?.filter(
          (opt) => opt.link !== SOW_LINK
        ),
      }));
    }

    return [{ key: "main", label: null, items: links.map(toItem) }];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, user, workspaceBrand, basePath]);

  const workspace = useMemo<SideNavWorkspace | null>(() => {
    if (workspaceBrand) {
      return {
        name: workspaceBrand.name,
        logo: workspaceBrand.logo,
        exit: { link: `/brands/${workspaceBrand.id}/details`, label: 'Exit workspace' },
      };
    }
    const brands: any[] = Array.isArray(brand) ? brand : [];
    if ((isBrand(role) || isBrandSales(role)) && brands[0]) {
      // Main-design workspace card (TDC-1499): brand name + logo, no
      // white-label accent/system-name. Prefer the network brand for
      // multi-brand RMN users so the card names their portal's root.
      const b = brands.find((x) => x && x.rmn) || brands[0];
      return { name: b.name, logo: b.logo };
    }
    return null;
  }, [brand, role, workspaceBrand]);

  const account = useMemo<SideNavAccount | null>(() => {
    if (!user) return null;
    const name =
      (user.displayName as string) ||
      (user.name as string) ||
      (user.email as string) ||
      "Account";
    const roleLabel = role ? titleizeName(String(SystemRoles[role as any])) : "";
    return {
      name,
      role: roleLabel,
      avatar: (user.avatar as string) || null,
      initials: initialsFromName(name) || "?",
    };
  }, [user, role]);

  const helpLink = isExpert(role) ? "/expert-resources" : "/tutorials";

  return { sections, workspace, account, helpLink, activePath: location.pathname };
}

// Active-route matching mirrors the old header (prefix match) but is slash-safe
// so "/video" doesn't light up "/videos".
export function isLinkActive(link: string, path: string): boolean {
  if (!link) return false;
  return path === link || path.startsWith(link + "/");
}

// Longest matching link wins, so flat siblings that share a prefix
// ("/experts" vs "/experts/featured") don't both light up.
export function bestActiveLink(links: string[], path: string): string | null {
  return links.reduce<string | null>(
    (best, link) =>
      link && isLinkActive(link, path) && (!best || link.length > best.length)
        ? link
        : best,
    null
  );
}
